/**
 * BarDataService — orchestrator for fetching OHLCV bar data.
 *
 * Multiple sources can be registered (NinjaTrader, Tradovate, Quantower,
 * Polygon, Databento, Local CSV, etc.). On `fetchBars()` the service:
 *   1. Checks the cache (memory + IndexedDB) → returns immediately on hit
 *   2. Walks registered sources in ascending priority order
 *   3. Returns the first source's bars, caches them, and tags the result
 *      with which source supplied them (for telemetry / debug overlays)
 *   4. Returns an empty array + a structured "miss" reason if nothing succeeds
 *
 * Sources are pure adapters — they don't touch the cache. The service owns
 * caching policy so swapping a source out doesn't affect what's already
 * been pulled.
 *
 * Source contract:
 *   class XYZBarSource {
 *     name: string                 // 'NinjaTrader', 'Local CSV', etc.
 *     priority: number              // lower = tried first
 *     async isAvailable(): boolean  // can this source even attempt a fetch?
 *     describeStatus(): {           // for the Settings → Charts UI
 *       ready: boolean,
 *       message?: string            // one-liner explaining readiness
 *     }
 *     async fetchBars(req): Bar[] | null
 *       // req: { symbol, timeframe, dateStr, fromUnixSec?, toUnixSec? }
 *       // Return null OR throw to defer to the next source.
 *       // Empty array means "I checked, nothing here" (still deferred).
 *   }
 */

import { BarCache } from './BarCache';
import { getCurrentTradingDay } from '../timeService';
import { detectBarGaps } from './gapDetect';

// Map timeframe label → expected bar period (seconds). Used to compute
// gap metadata after a fetch. Kept here (rather than imported from
// counterSignals) so the bar-data layer has no upward dependency on
// analyzer code.
const TF_TO_SECONDS = Object.freeze({
  '1m': 60, '3m': 180, '5m': 300, '15m': 900, '30m': 1800,
  '1h': 3600, '60m': 3600, '4h': 14400, '240m': 14400, '1d': 86400,
});

// Cache TTL for the CURRENT trading day. Past days' bars are immutable so
// they cache forever; today's bars are still being appended by JournifiBars
// (or whichever source is live), so a cache hit older than this window
// triggers a re-fetch. 60s is the sweet spot: fresh enough that opening a
// trade modal mid-session shows recent bars, but coarse enough that
// rapid-fire chart pans/zooms inside the same session don't hammer the IPC
// layer (which reads the CSV from disk on every call).
const LIVE_DAY_CACHE_TTL_MS = 60 * 1000;

class BarDataServiceImpl {
  constructor() {
    this.sources = new Map(); // name → source instance
    this.cache = new BarCache();
    // Listeners for source-list changes (Settings UI subscribes)
    this.listeners = new Set();
  }

  /**
   * Register a source. Replaces any existing source with the same name.
   */
  register(source) {
    if (!source || !source.name || typeof source.fetchBars !== 'function') {
      console.warn('[BarDataService] Invalid source — skipped:', source);
      return;
    }
    this.sources.set(source.name, source);
    this._notify();
  }

  unregister(name) {
    if (this.sources.delete(name)) this._notify();
  }

  /**
   * List all registered sources sorted by priority (lowest first = tried first).
   * Each entry: { name, priority, ready, message }.
   */
  listSources() {
    return Array.from(this.sources.values())
      .map(s => {
        const status = (typeof s.describeStatus === 'function')
          ? s.describeStatus()
          : { ready: true };
        return {
          name: s.name,
          priority: typeof s.priority === 'number' ? s.priority : 100,
          ready: !!status.ready,
          message: status.message || null,
        };
      })
      .sort((a, b) => a.priority - b.priority);
  }

  subscribe(fn) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  _notify() {
    for (const fn of this.listeners) {
      try { fn(this.listSources()); } catch { /* listener exceptions don't stop the chain */ }
    }
  }

  /**
   * Fetch bars for the given request, walking sources until one returns data.
   *
   * @param {Object} req
   * @param {string} req.symbol      e.g. 'MNQ'
   * @param {string} req.timeframe   '1m' | '5m' | '15m' | '1h'
   * @param {string} req.dateStr     YYYY-MM-DD trading day
   * @param {Object} [opts]
   * @param {boolean} [opts.skipCache]  Force-fetch from sources, ignoring cache.
   * @returns {Promise<{ bars: Bar[], source: string|null, fromCache: boolean, gaps: Gap[] }>}
   *
   * The `gaps` array reports bar-feed dropouts (delta between consecutive
   * bars exceeds 1.5× the expected period). Consumers like LiveTradePanel
   * use this to detect when a live trade's entry timestamp falls inside a
   * gap and trigger a cache-bust refetch as a healing attempt.
   *
   * `gaps` is always an array — empty when the feed is contiguous, or
   * when the timeframe is unknown / has no canonical period.
   */
  async fetchBars(req, opts = {}) {
    const { symbol, timeframe, dateStr } = req || {};
    if (!symbol || !timeframe || !dateStr) {
      return { bars: [], source: null, fromCache: false, gaps: [], error: 'missing-params' };
    }

    const cacheKey = `${symbol}|${dateStr}|${timeframe}`;
    const periodSec = TF_TO_SECONDS[timeframe] || null;

    // Today's trading day is special: bars are still being appended by
    // whatever source is live (JournifiBars writing 1m bars in real time,
    // typically). A cache hit from 5 minutes ago will be missing the most
    // recent action — exactly the "chart not updating" symptom users hit.
    // For today's date we apply a TTL; past days are immutable and cache
    // forever as before.
    const isLiveDay = (() => {
      try { return dateStr === getCurrentTradingDay(); }
      catch { return false; }
    })();

    // 1. Cache check (unless explicitly skipped)
    if (!opts.skipCache) {
      const cached = await this.cache.get(cacheKey);
      if (cached && Array.isArray(cached.bars) && cached.bars.length > 0) {
        const ageMs = Date.now() - (cached.fetchedAt || 0);
        // Use `>=` so a refresh tick aligned with the cache write moment
        // (both at multiples of LIVE_DAY_CACHE_TTL_MS) actually triggers
        // re-fetch rather than getting stuck one tick behind.
        const stale = isLiveDay && ageMs >= LIVE_DAY_CACHE_TTL_MS;
        if (!stale) {
          // Re-compute gaps from cached bars — cheap (one scan) and means
          // callers don't need to re-derive period themselves. We don't
          // cache the gap list because the cached bars are immutable for
          // the TTL window so the gap computation is deterministic anyway.
          const gaps = periodSec ? detectBarGaps(cached.bars, periodSec) : [];
          return { bars: cached.bars, source: cached.source, fromCache: true, gaps };
        }
        // Live-day cache is stale — fall through to re-fetch from sources.
        // Keep the existing entry around as a fallback below if all sources fail.
      }
    }

    // 2. Walk sources by priority
    const ordered = this.listSources(); // already sorted
    for (const meta of ordered) {
      const source = this.sources.get(meta.name);
      if (!source) continue;
      try {
        if (typeof source.isAvailable === 'function') {
          const ok = await source.isAvailable();
          if (!ok) continue;
        }
        const bars = await source.fetchBars(req);
        if (Array.isArray(bars) && bars.length > 0) {
          // 3. Cache + return (with gap metadata)
          await this.cache.set(cacheKey, { bars, source: source.name, fetchedAt: Date.now() });
          const gaps = periodSec ? detectBarGaps(bars, periodSec) : [];
          return { bars, source: source.name, fromCache: false, gaps };
        }
      } catch (err) {
        console.warn(`[BarDataService] Source "${source.name}" failed for ${cacheKey}:`, err?.message || err);
        // Fall through to next source
      }
    }

    return { bars: [], source: null, fromCache: false, gaps: [], error: 'no-source-returned-data' };
  }

  /**
   * Drop the cache entry for a specific request (used by "refresh chart"
   * actions). Doesn't trigger a refetch — caller does that next.
   */
  async invalidate(req) {
    const { symbol, timeframe, dateStr } = req || {};
    if (!symbol || !timeframe || !dateStr) return;
    await this.cache.delete(`${symbol}|${dateStr}|${timeframe}`);
  }

  /**
   * Drop the entire cache. For "Settings → Storage → Clear cache" UX or
   * when the user changes a source's config in a way that affects bar shape.
   */
  async clearCache() {
    await this.cache.clear();
  }

  /**
   * Cache stats for the Settings UI (entry count, approximate size on disk).
   */
  async cacheStats() {
    return this.cache.stats();
  }
}

// Singleton — sources register on app boot, the chart calls fetchBars from anywhere.
export const barDataService = new BarDataServiceImpl();

// Export the class too for tests / advanced usage
export { BarDataServiceImpl };
